import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useTenantKey } from './useTenantKey';

/**
 * Another member's avatar as an `<img src>`, from the tenant-scoped `/api/users/:id/avatar` proxy.
 *
 * The proxy wants the bearer token and X-Tenant, so a plain `<img>` cannot load it; this fetches it
 * as a blob. The blob is shared through the query cache because the same teammate turns up on many
 * table rows and topic chips at once, and each would otherwise fetch it again. Every caller makes its
 * own object URL from it and revokes it on unmount. Null while loading, when there is no photo, or on
 * failure — the caller shows initials.
 */
export function useMemberAvatar(avatarUrl: string | null): string | null {
  const tenantKey = useTenantKey();
  const { data: blob } = useQuery({
    queryKey: ['member-avatar', tenantKey, avatarUrl],
    queryFn: ({ signal }) => api.fetchAssetBlob(avatarUrl as string, signal),
    enabled: Boolean(avatarUrl) && tenantKey !== '',
    // The proxy itself allows five minutes of private caching; match it.
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setSrc(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  return src;
}
